import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, forkJoin, Observable, of as observableOf, throwError} from 'rxjs';
import {catchError, filter, map, takeUntil} from 'rxjs/operators';
import moment from 'moment';

import {
    ApplicationShortcutModel,
    OrganizationShortcutModel,
    ShortcutModel
} from '@app/core/model/search-result';
import {environment} from '@env/environment';
import {
    getAuthenticatedUser,
    getProviderApplications, isAuthenticated,
    State
} from '@app/app.reducers';
import {User} from '@app/core/model/user';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {ApplicationsService} from '../admin/provider/applications/applications.service';
import {ApplicationProviderListModel} from '../admin/application-settings/models/application-provider-list.model';
import {OrgsService} from '@app/core/service/orgs.service';

@Injectable({
    providedIn: 'root'
})
export class NgcSearchService {

    readonly KEY_RECENT_SCH_DISTR: string = 'key-recent-school-district';
    readonly KEY_RECENT_APPS: string = 'key-recent-apps';
    readonly KEY_CONTEXT_DISTR = 'search_cntx_district';
    readonly KEY_CONTEXT_FILTER = 'search_cntx_filter';
    readonly DURATION_OF_STORAGE_RECENT_ITEM = 14; // 14 days

    onSearchTermChanged = new EventEmitter<any>();
    onSearchContextChanged = new EventEmitter<any>();
    public searchContext = {
        district: null,
        filter: 'ALL'
    };

    public _recentDistricts: ShortcutModel[] = [];
    public _recentApplications: ShortcutModel[] = [];
    public recentDistrictsChanges: BehaviorSubject<any> = new BehaviorSubject(this._recentDistricts);
    public recentApplicationsChanges: BehaviorSubject<any> = new BehaviorSubject(this._recentApplications);
    private isRecentAppsAndOrgsCheckedForCurrentUser = false;
    user: User;

    constructor(
        private http: HttpClient,
        private authenticatedUserService: AuthenticatedUserService,
        private applicationsService: ApplicationsService,
        private orgsService: OrgsService,
        private store: Store<State>
    ) {

        this.store.pipe(
            select(getAuthenticatedUser),
            filter(user => !user)
        ).subscribe(user => {
            this.isRecentAppsAndOrgsCheckedForCurrentUser = false;
        });

        this.recentDistrictsChanges.subscribe(arr => {
            if (this.user) {
                window.localStorage.setItem(this.KEY_RECENT_SCH_DISTR + this.user.username,
                    JSON.stringify(this._recentDistricts));
            }
        });

        this.recentApplicationsChanges.subscribe(arr => {
            if (this.user) {
                window.localStorage.setItem(this.KEY_RECENT_APPS + this.user.username,
                    JSON.stringify(this._recentApplications));
            }
        });
    }

    restoreRecent(user: User) {
        if (user) {
            if (this.authenticatedUserService.isVendor(user) || this.authenticatedUserService.isSysAdmin(user)) {
                this._recentDistricts = [];
                const dcs = window.localStorage.getItem(this.KEY_RECENT_SCH_DISTR + user.username);
                if (dcs) {
                    this._recentDistricts = JSON.parse(dcs);
                }
                this.recentDistrictsChanges.next(this._recentDistricts);
            } else {
                this._recentApplications = [];
                // restoring recent applications
                const dcs = window.localStorage.getItem(this.KEY_RECENT_APPS + user.username);
                if (dcs) {
                    this._recentApplications = JSON.parse(dcs);
                }
                this.recentApplicationsChanges.next(this._recentApplications);
            }
        }
    }

    getActiveApps(): Observable<ApplicationShortcutModel[]> {
        return this.store.pipe(select(getProviderApplications)).pipe(
            filter((applications: ApplicationProviderListModel[]) => !!applications),
            map(arr => arr.map((a: ApplicationProviderListModel) => <ApplicationShortcutModel>{
                guid: a.guid,
                country: a.country,
                name: a.name,
                type: 'application',
                vendorName: a.vendorName,
                icon: a.icon,
                integrationId: a.integrationId
            })), );
    }

    setContextDistrict(district: any) {
        // disable district search context
        // this.searchContext.district = district;
        // this.onSearchContextChanged.emit(this.searchContext);
        this.serializeSearchContext();
    }
    setContextFilter(filter: string) {
        this.searchContext.filter = filter;
        this.onSearchContextChanged.emit(this.searchContext);
        this.serializeSearchContext();
    }

    getActivatedDistricts(applicationId: string): Observable<OrganizationShortcutModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/global_search/myInstitutions?applicationId=${applicationId}`;
        return this.http
            .get<OrganizationShortcutModel[]>(url)
            .pipe(
                map((data: OrganizationShortcutModel[]) => {
                    data.forEach((item: OrganizationShortcutModel) => {
                        item.type = item.orgType;
                    });
                    return data;
                }),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated))),
                catchError(this.handleError)
            );
    }

    getRecentSchools(): Observable<OrganizationShortcutModel[]> {
        return observableOf([]);
    }

    checkRecentAppsAndOrgs(user: User, currentApp: string = '') {
        this.user = user;

        if (this.user && !this.authenticatedUserService.isSysAdmin(this.user) && !this.isRecentAppsAndOrgsCheckedForCurrentUser) {
            this.deserializeSearchContext();
            this.onSearchContextChanged.emit(this.searchContext);

            // restoring recent apps or organizations
            this.restoreRecent(user);

            const recentItemsList$: Observable<any>[] = (this.authenticatedUserService.isVendor(this.user) ? this._recentDistricts : this._recentApplications)
                .filter((item: ShortcutModel) => {
                    const isItemOutdated = item['lastVisitDate'] &&
                       moment(new Date()).diff(item.lastVisitDate, 'day') > this.DURATION_OF_STORAGE_RECENT_ITEM;

                    if (isItemOutdated) {
                        this.removeFromRecentByGuid(item.guid);
                    }

                    return !isItemOutdated;
                })
                .map(item => this.search({term: item.guid, applicationId: currentApp})
                    .pipe(map(result => new Object({searchResult: result, itemGuid: item.guid})))
                );

            forkJoin(recentItemsList$).subscribe((recentItemsArray: {searchResult: ShortcutModel[]; itemGuid: string}[]) => {
                recentItemsArray.forEach(recentItem => {
                    if (recentItem && recentItem.searchResult.length == 0) {
                        this.removeFromRecentByGuid(recentItem.itemGuid);
                    }
                });
                this.isRecentAppsAndOrgsCheckedForCurrentUser = true;
            }, error => {
                console.log(error);
            });
        }
    }

    search(opt): Observable<ShortcutModel[]> {
        const term = encodeURIComponent(opt.term);
        let url = `${environment.datahubBaseURI}/api/v2/global_search?applicationId=${opt.applicationId}&search=${term}`;
        /* if (districtGuid) {
           url += '&districtGuid=' + districtGuid;
        }*/
        if (opt.page) {
            url += '&page=' + opt.page;
        }
        if (opt.pageSize) {
            url += '&pageSize=' + opt.pageSize;
        }

        return this.http.get<ShortcutModel[]>(url)
            .pipe(
                map((data: ShortcutModel[]) => {
                    data.forEach((item: ShortcutModel) => {
                        if (item.type === 'application') {

                        } else {
                            // type === 'organization'
                            const org: OrganizationShortcutModel = <OrganizationShortcutModel>item;
                            item.type = org.orgType;
                        }
                    });
                    return data;
                }),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated))),
                catchError(this.handleError)
            );
    }

    addToRecent(entity: ShortcutModel) {
        entity.lastVisitDate = new Date().toISOString();

        if (this.authenticatedUserService.isVendor(this.user) || this.authenticatedUserService.isSysAdmin(this.user)) {
            const recentDistrictIndex = this._recentDistricts.findIndex(d => d.guid === entity.guid);
            if (recentDistrictIndex < 0) {
                if (this._recentDistricts.length >= 5) {
                    this._recentDistricts.shift();
                }
                this._recentDistricts.push(entity);
                this.recentDistrictsChanges.next(this._recentDistricts);
            } else {
                this._recentDistricts[recentDistrictIndex] = entity;
                this.recentDistrictsChanges.next(this._recentDistricts);
            }
        } else {
            const recentApplicationIndex = this._recentApplications.findIndex(d => d.guid === entity.guid);
            if (recentApplicationIndex < 0) {
                if (this._recentApplications.length >= 5) {
                    this._recentApplications.shift();
                }
                this._recentApplications.push(entity);
                this.recentApplicationsChanges.next(this._recentApplications);
            } else {
                this._recentApplications[recentApplicationIndex] = entity;
                this.recentApplicationsChanges.next(this._recentApplications);
            }
        }
    }
    removeFromRecentByGuid(entityGuid: string) {
        this._recentDistricts = this._recentDistricts.filter(d => d.guid !== entityGuid);
        this._recentApplications = this._recentApplications.filter(d => d.guid !== entityGuid);

        if (this.authenticatedUserService.isVendor(this.user) || this.authenticatedUserService.isSysAdmin(this.user)) {
            this.recentDistrictsChanges.next(this._recentDistricts);
        } else {
            this.recentApplicationsChanges.next(this._recentApplications);
        }
    }

    private deserializeSearchContext() {
        if (this.user) {
            const key1 = this.KEY_CONTEXT_DISTR + this.user.username;
            const key2 = this.KEY_CONTEXT_FILTER + this.user.username;

            const dcs = window.localStorage.getItem(key1);
            if (dcs) {
                try {
                    this.searchContext.district = JSON.parse(dcs);
                } catch (e) {
                }
            }
            this.searchContext.filter = window.localStorage.getItem(key2);

            if (!this.searchContext.filter) {
                this.searchContext.filter = 'ALL';
            }
        }
    }

    private serializeSearchContext() {
        if (this.user) {
            const key1 = this.KEY_CONTEXT_DISTR + this.user.username;
            const key2 = this.KEY_CONTEXT_FILTER + this.user.username;

            if (this.searchContext.district) {
                window.localStorage.setItem(key1, JSON.stringify(this.searchContext.district));
            } else {
                window.localStorage.removeItem(key1);
            }

            window.localStorage.setItem(key2, this.searchContext.filter);
        }
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.message}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }

    unSubscribeOnSearchTermChanged() {
        this.onSearchTermChanged.unsubscribe();
    }
}
